// @flow weak
/*
Popover makes a little bubble of content that points to a target element. Use
like:

<Popover>
  <MyTargetComponent/>
  <MyPopoverContent/>
</Popover>

If you specify the second child, the popover will appear, with an arrow
pointing to the target component (as rendered to the DOM). Popover uses a
custom portal, so you don't have to worry about it mucking with the markup.
Popover will just render the target component as-is, with no wrapping div or
span.
*/

import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import createReactClass from 'create-react-class'; // eslint-disable-line no-restricted-syntax
import bemify, { toModifiers } from 'bemify-js';

import { Zindexes } from '@zapier/theme';

import Icon from 'app/common/components/Icon';
import PopoverArrow from 'app/common/components/PopoverArrow';
import watchWindowSize from 'app/common/components/watchWindowSize';
import { subscribeAnimationFrame } from 'app/common/CommonUtils';
import { getRef, setRef } from 'app/common/utils/ReactUtils';
import 'app/common/components/Popover.scss';

const EDGE_BUFFER = 5;
const bem = bemify('popover');

// The actual displayable Popover.
const Popover = watchWindowSize(
  createReactClass({
    displayName: 'Popover',
    isUpdatingFromMeasure: (null: ?boolean),

    getInitialState() {
      return {
        contentWidth: 0,
        contentHeight: 0,
        arrowUpWidth: 0,
        arrowUpHeight: 0,
      };
    },

    measure() {
      // Prevent measuring because of an update caused by measuring. Because of
      // minor differences in measurement after moving the Popover, you can get
      // into an infinite loop if you don't use this lock.
      if (this.isUpdatingFromMeasure) {
        return;
      }
      // Measure all the pieces.
      const newState = [
        'content',
        'arrowUp',
        'arrowRight',
        'arrowLeft',
        'arrowDown',
      ].reduce((stateObj, refKey) => {
        // Get the node for the piece.
        const node = getRef(this, refKey);
        if (node) {
          // We want fractions of pixels, so can't just ask for `offsetWidth`
          // and `offsetHeight`.
          const rect = node.getBoundingClientRect();
          const offsetWidth = rect.right - rect.left;
          const offsetHeight = rect.bottom - rect.top;
          // Compare size to size in state.
          if (
            this.state[`${refKey}Width`] !== offsetWidth ||
            this.state[`${refKey}Height`] !== offsetHeight
          ) {
            // If there's a change, start building a state object.
            stateObj = stateObj || {};
            stateObj[`${refKey}Width`] = offsetWidth;
            stateObj[`${refKey}Height`] = offsetHeight;
          }
        }
        return stateObj;
      }, null);

      // If we have a new state object, it means something has changed sizes.
      if (newState) {
        // Lock against calling measure while updating the state.
        this.isUpdatingFromMeasure = true;
        this.setState(newState, () => {
          // Next update will get called before this.
          this.isUpdatingFromMeasure = false;
        });
      }
    },

    componentDidMount() {
      this.measure();
    },

    componentDidUpdate() {
      this.measure();
    },

    // Find out where the target is, and create an optimal position for our
    // popover.
    calculatePosition() {
      const {
        targetTop,
        targetRight,
        targetBottom,
        targetLeft,
        windowWidth,
        windowHeight,
      } = this.props;
      const {
        contentWidth,
        contentHeight,
        arrowUpWidth,
        arrowUpHeight,
        arrowDownWidth,
        arrowDownHeight,
      } = this.state;
      const targetCenterX = targetLeft + (targetRight - targetLeft) / 2;
      //const targetCenterY = targetTop + ((targetBottom - targetTop) / 2);
      const contentLeftFromCenter = targetCenterX - contentWidth / 2;
      const contentLeftSafeRight =
        contentLeftFromCenter + contentWidth > windowWidth + EDGE_BUFFER
          ? windowWidth - contentWidth - EDGE_BUFFER
          : contentLeftFromCenter;
      const contentLeft =
        contentLeftSafeRight > EDGE_BUFFER ? contentLeftSafeRight : EDGE_BUFFER;
      const contentTopFromBottom = targetBottom + arrowUpHeight;
      let contentTop = contentTopFromBottom;
      let isArrowUp = true;
      if (
        contentTopFromBottom + contentHeight + arrowUpHeight >
        windowHeight - EDGE_BUFFER
      ) {
        contentTop = targetTop - contentHeight - arrowDownHeight;
        isArrowUp = false;
      }
      const arrowTop = isArrowUp ? targetBottom : targetTop - arrowDownHeight;
      const arrowWidth = isArrowUp ? arrowUpWidth : arrowDownWidth;
      const position = {
        contentTop,
        contentLeft,
        arrowTop,
        arrowLeft: targetCenterX - arrowWidth / 2,
        isArrowUp,
      };
      if (this.props.isAttached) {
        position.contentTop =
          this.props.attachTop + position.contentTop - targetTop;
        position.contentLeft =
          this.props.attachLeft + position.contentLeft - targetLeft;
        position.arrowTop =
          this.props.attachTop + position.arrowTop - targetTop;
        position.arrowLeft =
          this.props.attachLeft + position.arrowLeft - targetLeft;
      }
      return position;
    },

    // Abstracted a bit, because we need to write out content twice.
    renderContent(refKey, style, onClose) {
      return (
        <div
          className={bem('__content-container')}
          ref={setRef(this, refKey)}
          style={style}
        >
          <div className={bem('__content')}>{this.props.children}</div>
          {!this.props.onClose ? null : (
            <Icon
              className={bem('__close-button no-select')}
              onClick={onClose}
              type="delete"
            />
          )}
        </div>
      );
    },

    render() {
      const { contentWidth, contentHeight } = this.state;
      const position = this.calculatePosition();
      const style = _.extend(
        {},
        {
          position: this.props.isAttached ? 'absolute' : 'fixed',
          zIndex: Zindexes.popover,
        }
      );
      if (this.state.contentWidth < 1 && this.state.contentHeight < 1) {
        style.visibility = 'hidden';
      }
      const contentStyle = _.extend({}, style, {
        top: position.contentTop,
        left: position.contentLeft,
        width: contentWidth,
        height: contentHeight,
      });
      const arrowUpStyle = _.extend({}, style, {
        top: position.arrowTop,
        left: position.arrowLeft,
        zIndex: Zindexes.popover + 1,
        visibility: position.isArrowUp ? '' : 'hidden',
      });
      const arrowDownStyle = _.extend({}, style, {
        top: position.arrowTop,
        left: position.arrowLeft,
        zIndex: Zindexes.popover + 1,
        visibility: position.isArrowUp ? 'hidden' : '',
      });
      // Content clone for stable measuring. Keep it hidden.
      const contentCopyStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        visibility: 'hidden',
        zIndex: -1,
      };
      return (
        // This alert role is probably not accurate in all cases. We should use
        // aria-describedby in some cases, but the alert role at least makes it
        // known when there's an interaction, albeit possibly extra noisy in
        // some cases.
        <div
          className={bem(toModifiers(this.props.kind))}
          onMouseEnter={this.props.onMouseEnter}
          onMouseLeave={this.props.onMouseLeave}
          role="alert"
        >
          {/* render copy of content for measuring at position 0, 0 */}
          {this.renderContent('content', contentCopyStyle)}
          <div
            aria-hidden="true"
            ref={setRef(this, 'arrowUp')}
            style={arrowUpStyle}
          >
            <PopoverArrow className={bem('__arrow')} direction="up" />
          </div>
          <div
            aria-hidden="true"
            ref={setRef(this, 'arrowDown')}
            style={arrowDownStyle}
          >
            <PopoverArrow className={bem('__arrow')} direction="down" />
          </div>
          {/* render actual content, which we're forcing to match the copy */}
          {this.renderContent(null, contentStyle, this.props.onClose)}
        </div>
      );
    },
  })
);

// The wrapper around the target, which renders a Popover in a portal.
const PopoverWrapper = createReactClass({
  displayName: 'PopoverWrapper',
  targetNode: (null: ?Element),
  portalNode: (null: ?Element),
  unsubscribeTargetAnimation: (null: ?any),
  mixins: [PureRenderMixin],

  contextTypes: {
    store: PropTypes.object,
  },

  getInitialState() {
    return {
      isOpen: false,
      isToggling: false,
    };
  },

  popoverContent() {
    if (_.isArray(this.props.children)) {
      return this.props.children[1];
    }
    return null;
  },

  hasPopoverContent() {
    return !!this.popoverContent();
  },

  findTargetNode() {
    let targetNode = (ReactDOM.findDOMNode(this): any);
    if (this.props.shouldAttach) {
      if (targetNode.childNodes.length > 0) {
        targetNode = targetNode.childNodes[0];
      }
    }
    return targetNode;
  },

  componentDidMount() {
    this.targetNode = this.findTargetNode();
    this.onUpdate();
  },

  componentDidUpdate() {
    this.targetNode = this.findTargetNode();
    this.onUpdate();
  },

  onUpdate() {
    if (this.state.isOpen && !this.hasPopoverContent()) {
      return this.close();
    }
    if (!this.state.isOpen && this.hasPopoverContent()) {
      return this.open();
    }
    this.refresh();
    return undefined;
  },

  componentWillUnmount() {
    this.cancelWatchingTarget();
    this.destroyPortal();
  },

  // Polling for target position while popover is open. Yucky, but safest way
  // to guarantee we know its current rect.
  watchTarget() {
    if (!this.unsubscribeTargetAnimation) {
      this.unsubscribeTargetAnimation = subscribeAnimationFrame(() => {
        this.onWatchTarget();
      });
    }
  },

  // Get the current rect for the target.
  onWatchTarget() {
    if (this.targetNode) {
      const targetRect = this.targetRect() || {};
      const lastTargetRect = this.state.targetRect || {};
      if (
        targetRect.top !== lastTargetRect.top ||
        targetRect.right !== lastTargetRect.right ||
        targetRect.bottom !== lastTargetRect.bottom ||
        targetRect.left !== lastTargetRect.left
      ) {
        if (this.props.onMove) {
          this.props.onMove();
        }
        this.setState({
          targetRect,
        });
      }
    }
  },

  // Just make sure target rect is a plain object.
  targetRect() {
    if (this.targetNode) {
      let rect = this.targetNode.getBoundingClientRect();
      if (this.props.onTargetRect) {
        rect = this.props.onTargetRect(rect);
      }
      const { top, right, bottom, left } = rect;
      return { top, right, bottom, left };
    }
    return null;
  },

  cancelWatchingTarget() {
    if (this.unsubscribeTargetAnimation) {
      this.unsubscribeTargetAnimation();
      this.unsubscribeTargetAnimation = null;
    }
  },

  open() {
    this.createPortal();
    this.refresh();
    this.watchTarget();
    this.setState({
      isOpen: true,
      targetRect: this.targetRect(),
    });
  },

  close() {
    this.cancelWatchingTarget();
    this.destroyPortal();
    this.setState({
      isOpen: false,
    });
  },

  // Something changed, so re-render the Popover too.
  refresh() {
    if (this.hasPopoverContent() && this.portalNode) {
      const targetRect = this.targetRect();
      if (targetRect) {
        const attachProps = {};
        if (this.props.shouldAttach) {
          const portalContainerRect = getRef(
            this,
            'portal'
          ).getBoundingClientRect();
          attachProps.attachTop = targetRect.top - portalContainerRect.top;
          attachProps.attachLeft = targetRect.left - portalContainerRect.left;
        }
        ReactDOM.unstable_renderSubtreeIntoContainer(
          this,
          <Popover
            isAttached={this.props.shouldAttach}
            kind={this.props.kind}
            onClose={this.props.onClose}
            onMouseEnter={this.props.onMouseEnter}
            onMouseLeave={this.props.onMouseLeave}
            targetBottom={targetRect.bottom}
            targetLeft={targetRect.left}
            targetRight={targetRect.right}
            targetTop={targetRect.top}
            {...attachProps}
          >
            {this.popoverContent()}
          </Popover>,
          this.portalNode
        );
      }
    }
  },

  portalContainer() {
    return getRef(this, 'portal') || document.body;
  },

  // Creating a custom portal so we don't have to wrap our target and popover
  // content in a junk div or span.
  createPortal() {
    if (!this.portalNode) {
      this.portalNode = document.createElement('div');
      this.portalContainer().appendChild(this.portalNode);
    }
  },

  destroyPortal() {
    if (this.portalNode) {
      ReactDOM.unmountComponentAtNode(this.portalNode);
      this.portalContainer().removeChild(this.portalNode);
      this.portalNode = null;
    }
  },

  // Pass through the first child.
  render() {
    const Tag = this.props.tag || 'span';
    let content = this.props.children || null;

    if (content && _.isArray(content)) {
      content = content[0];
    }

    if (this.props.shouldAttach) {
      return (
        <Tag style={{ position: 'relative' }}>
          {content}
          <div
            ref={setRef(this, 'portal')}
            style={{ position: 'absolute', left: 0, top: 0 }}
          />
        </Tag>
      );
    } else {
      return content;
    }
  },
});

export default PopoverWrapper;
